/** Minimal 2D vector math. Plain objects — small scenes, clarity over allocation tricks. */

export interface Vec {
  x: number;
  y: number;
}

export const vec = (x: number, y: number): Vec => ({ x, y });

export const add = (a: Vec, b: Vec): Vec => ({ x: a.x + b.x, y: a.y + b.y });
export const sub = (a: Vec, b: Vec): Vec => ({ x: a.x - b.x, y: a.y - b.y });
export const scale = (a: Vec, s: number): Vec => ({ x: a.x * s, y: a.y * s });
export const dot = (a: Vec, b: Vec): number => a.x * b.x + a.y * b.y;

/** 2D cross products: vector×vector is a scalar, scalar×vector is a vector. */
export const cross = (a: Vec, b: Vec): number => a.x * b.y - a.y * b.x;
export const crossSV = (s: number, v: Vec): Vec => ({ x: -s * v.y, y: s * v.x });
export const crossVS = (v: Vec, s: number): Vec => ({ x: s * v.y, y: -s * v.x });

export const len = (a: Vec): number => Math.hypot(a.x, a.y);
export const dist = (a: Vec, b: Vec): number => Math.hypot(a.x - b.x, a.y - b.y);

export function normalize(a: Vec): Vec {
  const l = Math.hypot(a.x, a.y);
  return l > 1e-9 ? { x: a.x / l, y: a.y / l } : { x: 0, y: 0 };
}

/** Rotate `v` by `angle` radians about the origin. */
export function rotate(v: Vec, angle: number): Vec {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return { x: v.x * c - v.y * s, y: v.x * s + v.y * c };
}

/** Local-space point -> world space, given a body's position and angle. */
export function toWorld(local: Vec, pos: Vec, angle: number): Vec {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return { x: pos.x + local.x * c - local.y * s, y: pos.y + local.x * s + local.y * c };
}

export const clamp = (n: number, min: number, max: number): number =>
  n < min ? min : n > max ? max : n;

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
